import type { Action, ThunkAction } from '@reduxjs/toolkit';
import { configureStore } from '@reduxjs/toolkit';

import voiceNoteReducer from '@/store/reducers/voiceNote';
import accountReducer from '@/store/reducers/account';
import ordersReducer from '@/store/reducers/orders';
import orderReducer from '@/store/reducers/order';
import userReducer from '@/store/reducers/user';
import favoritesReducer from '@/store/reducers/favorites';
import sectionReducer from '@/store/reducers/section';
import cartReducer from '@/store/reducers/cart';
import storeCartReducer from '@/store/reducers/storeCart';
import shipMethodsReducer from '@/store/reducers/shippingMethods';
import galleryFiltersReducer from '@/store/reducers/galleryFilters';
import designsReducer from '@/store/reducers/designs';
import invoiceReducer from '@/store/reducers/invoice';
import braintreeReducer from '@/store/reducers/braintree';

import { accountsQuery } from '@/api/AccountsQuery';
import { userQuery } from '@/api/UserQuery';
import { fetchSchoolsApi, fetchOrganizationsApi } from '@/api/CollegiateDataQuery';
import { mostRecentApi } from '@/api/MostRecentQuery';
import { mostRecentAdressApi } from '@/api/MostRecentAddressQuery';
import { orderApi } from '@/api/OrderQuery';
import { favoriteQuery } from '@/api/FavoriteQuery';
import { invoiceQuery } from '@/api/InvoiceQuery';
import { designsApi } from '@/api/DesignQuery';
import { designsLocalApi } from '@/api/DesignApiServerQuery';
import { filterQuery } from '@/api/FilterQuery';
import { orderInvoiceApi } from '@/api/OrderInvoiceQuery';
import { productsApi } from '@/api/Products';
import { s3QueryApi } from '@/api/S3Query';
import { cartQuery } from '@/api/CartQuery';
import { checkoutCartQuery } from '@/api/CheckoutCartQuery';
import { fetchQuoterApi } from '@/api/QuoterQuery';
import { promoCodeQuery } from '@/api/PromoCodeQuery';
import { promoCodeUserQuery } from '@/api/PromoCodeUserQuery';
import { productInfoQuery } from '@/api/ProductInfoQuery';
import { orderReviewQuery } from '@/api/OrderReviewQuery';
import { topbarDataQuery } from '@/api/TopbarDataQuery';
import { braintreePaypalQuery } from '@/api/BraintreePaypalQuery';
import { stockQuery } from '@/api/stockQuery';
import { similarProductApi } from '@/api/SimilarProductQuery';
import { fetchTimelineApi } from '@/api/timelineQuery';
import { storeApi } from '@/api/StoreQuery';
import { participantEditQuery } from '@/api/ParticipantEditQuery';
import { productSizeChartQuery } from '@/api/ProductSizeChartQuery';
import { participantHqQuery } from '@/api/ParticipantHqQuery';
import { printConfigQuery } from '@/api/PrintConfigQuery';

export const store = configureStore({
  reducer: {
    account: accountReducer,
    orders: ordersReducer,
    order: orderReducer,
    user: userReducer,
    favorites: favoritesReducer,
    section: sectionReducer,
    cart: cartReducer,
    storeCart: storeCartReducer,
    shipMethods: shipMethodsReducer,
    galleryFilters: galleryFiltersReducer,
    designs: designsReducer,
    voiceNote: voiceNoteReducer,
    invoice: invoiceReducer,
    braintree: braintreeReducer,
    [userQuery.reducerPath]: userQuery.reducer,
    [fetchSchoolsApi.reducerPath]: fetchSchoolsApi.reducer,
    [fetchOrganizationsApi.reducerPath]: fetchOrganizationsApi.reducer,
    [accountsQuery.reducerPath]: accountsQuery.reducer,
    [mostRecentApi.reducerPath]: mostRecentApi.reducer,
    [mostRecentAdressApi.reducerPath]: mostRecentAdressApi.reducer,
    [orderApi.reducerPath]: orderApi.reducer,
    [favoriteQuery.reducerPath]: favoriteQuery.reducer,
    [invoiceQuery.reducerPath]: invoiceQuery.reducer,
    [designsApi.reducerPath]: designsApi.reducer,
    [designsLocalApi.reducerPath]: designsLocalApi.reducer,
    [filterQuery.reducerPath]: filterQuery.reducer,
    [orderInvoiceApi.reducerPath]: orderInvoiceApi.reducer,
    [productsApi.reducerPath]: productsApi.reducer,
    [s3QueryApi.reducerPath]: s3QueryApi.reducer,
    [cartQuery.reducerPath]: cartQuery.reducer,
    [checkoutCartQuery.reducerPath]: checkoutCartQuery.reducer,
    [fetchQuoterApi.reducerPath]: fetchQuoterApi.reducer,
    [promoCodeQuery.reducerPath]: promoCodeQuery.reducer,
    [promoCodeUserQuery.reducerPath]: promoCodeUserQuery.reducer,
    [productInfoQuery.reducerPath]: productInfoQuery.reducer,
    [orderReviewQuery.reducerPath]: orderReviewQuery.reducer,
    [topbarDataQuery.reducerPath]: topbarDataQuery.reducer,
    [braintreePaypalQuery.reducerPath]: braintreePaypalQuery.reducer,
    [stockQuery.reducerPath]: stockQuery.reducer,
    [similarProductApi.reducerPath]: similarProductApi.reducer,
    [fetchTimelineApi.reducerPath]: fetchTimelineApi.reducer,
    [storeApi.reducerPath]: storeApi.reducer,
    [participantEditQuery.reducerPath]: participantEditQuery.reducer,
    [participantHqQuery.reducerPath]: participantHqQuery.reducer,
    [productSizeChartQuery.reducerPath]: productSizeChartQuery.reducer,
    [printConfigQuery.reducerPath]: printConfigQuery.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false,
    }).concat([
      userQuery.middleware,
      fetchSchoolsApi.middleware,
      fetchOrganizationsApi.middleware,
      accountsQuery.middleware,
      mostRecentApi.middleware,
      mostRecentAdressApi.middleware,
      orderApi.middleware,
      productsApi.middleware,
      favoriteQuery.middleware,
      invoiceQuery.middleware,
      designsApi.middleware,
      designsLocalApi.middleware,
      filterQuery.middleware,
      orderInvoiceApi.middleware,
      s3QueryApi.middleware,
      cartQuery.middleware,
      checkoutCartQuery.middleware,
      fetchQuoterApi.middleware,
      promoCodeQuery.middleware,
      promoCodeUserQuery.middleware,
      productInfoQuery.middleware,
      orderReviewQuery.middleware,
      topbarDataQuery.middleware,
      braintreePaypalQuery.middleware,
      stockQuery.middleware,
      similarProductApi.middleware,
      fetchTimelineApi.middleware,
      storeApi.middleware,
      participantEditQuery.middleware,
      participantHqQuery.middleware,
      productSizeChartQuery.middleware,
      printConfigQuery.middleware,
    ]),
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
